import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { FiArrowUpRight } from "react-icons/fi";
import useWindowSize from "../../../../hooks/useWindowSize";

const headCellSx = {
    fontWeight: 700,
    fontSize: 13,
    color: "#fff",
    backgroundColor: "var(--color-primary-background)",
    whiteSpace: "nowrap",
};

function formatDate(date) {
    return new Date(date).toLocaleDateString("pt-BR");
}

function getInitials(name = "") {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
}

// Status de cobrança calculado no backend (getCompaniesService), cruzando o trial, o controle
// de pagamento e o historico real de pagamentos confirmados (companies.payments_register).
// Nao usar paymentControl.status isoladamente aqui: ele e apenas uma flag de acesso (bloqueio de
// login), setada TRUE desde a criacao da empresa mesmo antes de qualquer cobranca real.
const BILLING_STATUS_META = {
    TRIALING: { color: "info", text: "Em teste" },
    NO_PAYMENT: { color: "default", text: "Sem pagamentos" },
    OVERDUE: { color: "error", text: "Inadimplente" },
    ACTIVE: { color: "success", text: "Em dia" },
};

function getCompanyInsights(item) {
    const paymentControl = item.payment_control || null;
    const trial = item.trial ? item.trial[0] : null;

    const deletedStatus = {
        color: item.deleted ? "error" : "success",
        text: item.deleted ? "Excluída" : "Ativa",
    };

    const operating = {
        color: item.operating ? "success" : "warning",
        text: item.operating ? "Válida" : "Em espera",
    };

    const payment = BILLING_STATUS_META[item.billing_status] || BILLING_STATUS_META.NO_PAYMENT;

    const overdue = item.billing_status === "OVERDUE";

    const nextPayment = paymentControl?.next_payment
        ? formatDate(paymentControl.next_payment)
        : "Sem dados";

    // `trial.status` so vira 'expired' via cron diario. Se o cron atrasar/falhar, um trial cujo
    // `end_date` ja passou continua com status='active' indefinidamente (mesmo problema ja
    // corrigido em computeBillingStatus.js). Por isso a checagem abaixo tambem considera o
    // end_date, em vez de confiar apenas na coluna de status.
    const trialPastEndDate = trial?.end_date ? new Date(trial.end_date) < new Date() : false;
    const trialCurrentlyActive = trial?.status === "active" && !trialPastEndDate;
    const trialFinishedWithoutConversion = !trial?.converted_to_plan && (trial?.status === "expired" || trialPastEndDate);

    const trialStatus = trial
        ? {
            color: trial?.converted_to_plan
                ? "success"
                : trialCurrentlyActive
                ? "warning"
                : trialFinishedWithoutConversion
                ? "error"
                : "default",
            text: trial?.converted_to_plan
                ? "Convertido"
                : trialCurrentlyActive
                ? "Ativo"
                : trialFinishedWithoutConversion
                ? "Expirado"
                : "Sem dados",
        }
        : { color: "default", text: "Sem trial" };

    return { paymentControl, deletedStatus, operating, payment, overdue, nextPayment, trialStatus };
}

function InfoRow({ label, value, valueColor }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600} color={valueColor} noWrap>{value}</Typography>
        </Stack>
    );
}

export default function ClientsList({ clients, openCompany }) {
    const width = useWindowSize();
    const isDesktop = width >= 1280;

    if (isDesktop) {
        return (
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 3, border: "1px solid #e3e6ea", width: '100%', maxHeight: "79vh" }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ ...headCellSx }} />
                            <TableCell sx={headCellSx} align="center">Empresa</TableCell>
                            <TableCell sx={{ ...headCellSx, width: 250 }} align="center">Razão social</TableCell>
                            <TableCell sx={headCellSx} align="center">CPF / CNPJ</TableCell>
                            <TableCell sx={headCellSx} align="center">Operante</TableCell>
                            <TableCell sx={headCellSx} align="center">Status</TableCell>
                            <TableCell sx={headCellSx} align="center">Em teste</TableCell>
                            <TableCell sx={headCellSx} align="center">Pagamento</TableCell>
                            <TableCell sx={headCellSx} align="center">Prox. pagamento</TableCell>
                            <TableCell sx={headCellSx} align="center">Registro</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {clients.map((item, index) => {
                            const { paymentControl, deletedStatus, operating, payment, overdue, nextPayment, trialStatus } = getCompanyInsights(item);

                            return (
                                <TableRow
                                    key={item.id ?? index}
                                    hover
                                    sx={{
                                        backgroundColor: index % 2 === 0 ? "#fff" : "#d9d9da",
                                        "&:last-child td": { borderBottom: 0 },
                                    }}
                                >
                                    <TableCell>
                                        <Tooltip title="Abrir empresa">
                                            <IconButton
                                                size="small"
                                                onClick={() => openCompany({ id: item.id, paymentControl })}
                                                sx={{
                                                    color: "var(--color-primary-background)",
                                                    border: "1px solid var(--color-primary-background)",
                                                    "&:hover": {
                                                        backgroundColor: "var(--color-primary-background)",
                                                        color: "#fff",
                                                    },
                                                }}
                                            >
                                                <FiArrowUpRight size={16} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    fontSize: 13,
                                                    bgcolor: "var(--color-primary-background)",
                                                }}
                                            >
                                                {getInitials(item.name)}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                                        </Stack>
                                    </TableCell>

                                    <TableCell style={{width: 250}}>
                                        <Typography variant="body2" color="text.secondary" style={{fontSize: 12}}>
                                            {item.razao_social || "Sem dados"}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                            {item.document}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip label={operating.text} color={operating.color} size="small" />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip label={deletedStatus.text} color={deletedStatus.color} size="small" variant={item.deleted ? "filled" : "outlined"} />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip label={trialStatus.text} color={trialStatus.color} size="small" variant="outlined" />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip label={payment.text} color={payment.color} size="small" />
                                    </TableCell>

                                    <TableCell>
                                        {overdue ? (
                                            <Chip label={nextPayment} color="error" size="small" />
                                        ) : (
                                            <Typography variant="body2">{nextPayment}</Typography>
                                        )}
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(item.created_at)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <Stack spacing={2}>
            {clients.map((item, index) => {
                const { paymentControl, deletedStatus, operating, payment, overdue, nextPayment, trialStatus } = getCompanyInsights(item);

                return (
                    <Card key={item.id ?? index} variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: "var(--color-primary-background)" }}>
                                {getInitials(item.name)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight={700} noWrap>{item.name}</Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {item.razao_social || "Sem dados"}
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                            <Chip label={operating.text} color={operating.color} size="small" />
                            <Chip label={deletedStatus.text} color={deletedStatus.color} size="small" variant={item.deleted ? "filled" : "outlined"} />
                            <Chip label={trialStatus.text} color={trialStatus.color} size="small" variant="outlined" />
                            <Chip label={payment.text} color={payment.color} size="small" />
                        </Stack>

                        <Divider sx={{ my: 1.5 }} />

                        <Stack spacing={1}>
                            <InfoRow label="CPF / CNPJ" value={item.document} />
                            <InfoRow
                                label="Próx. pagamento"
                                value={nextPayment}
                                valueColor={overdue ? "error.main" : undefined}
                            />
                            <InfoRow label="Registro" value={formatDate(item.created_at)} />
                        </Stack>

                        <Button
                            fullWidth
                            endIcon={<FiArrowUpRight />}
                            onClick={() => openCompany({ id: item.id, paymentControl })}
                            sx={{
                                mt: 2,
                                backgroundColor: "var(--color-primary-background)",
                                color: "#fff",
                                "&:hover": { backgroundColor: "var(--color-secondary-background)" },
                            }}
                        >
                            Abrir empresa
                        </Button>
                    </Card>
                );
            })}
        </Stack>
    );
}
